import React, { useState } from 'react';
import { Button, Col, Row } from 'reactstrap';
import { Modal, ModalHeader, Container, Card, CardTitle, CardBody, UncontrolledTooltip } from 'reactstrap';

import utility from '../../services/utility';
import queueService from '../../services/queueService';
import { useNotification } from '../../services/notification';
import mediaService from '../../services/mediaService';

import { FILE_STATUS } from '../../enums/fileStatus'
import { FILE_TYPE } from '../../enums/fileType';
import { PUBLISH_STATE, PUBLISH_STATE_ID } from '../../enums/publishState';

import KeyValueDisplay from '../../components/KeyValueDisplay/KeyValueDisplay';
import MediaDetails from './MediaDetails'
import CopyButton from '../../components/Controls/CopyButton';


const MediaInfoModal = ({ data, onClose, isOpen }) => {
    const { videoPlatformData, item } = data || {};
    const { showName: show } = item || {};

    const [queueProcessing, setQueueProcessing] = useState(videoPlatformData?.queueProcessing || false)

    const notification = useNotification();

    const itemData = {
        "Media Id": item.id,
        "Title": item.name,
        "File Type": item.fileType ? FILE_TYPE[item.fileType] : "N/A",
        "Duration": item.duration ? utility.formatMillisecondTime(item.duration) : "N/A",
        "Created Date": item.createdDate ? utility.formatDateTimeString(item.createdDate): "N/A",
        "Upload Status": item.uploadStatus ? FILE_STATUS[item.uploadStatus].name : "N/A",
        "Stream Status": item.streamStatus ? FILE_STATUS[item.streamStatus].name : "N/A",
    };

    const updateMediaDuration = (id, show) => {
        mediaService.updateDuration(show, id)
            .then(() => notification.success("Media duration updated successfully!"));
    }

    const onClickRequeue = (item) => queueService
        .requeue(item.id, item.showName)
        .then(() => {
            setQueueProcessing(true);
            notification.success("Media item successfully re-queued!")
        });

    const sourcePath = videoPlatformData?.sourcePath;
    const sourceFileExists = videoPlatformData?.sourceFileExists || false;

    const muxMedia = videoPlatformData?.media;
    const muxState = muxMedia?.state;
    const isMuxReady = muxState === PUBLISH_STATE_ID.READY;

    const updateDurationButtonText = !muxMedia
        ? "Cannot update duration without Mux file"
        : `Cannot update duration, file is ${PUBLISH_STATE[muxState] || "unavailable"} on Mux`;

    const requeueButton = (item) => {
        const muxExists = videoPlatformData && videoPlatformData.media;
        const canRequeue = muxExists || sourceFileExists;

        const text = (queueProcessing && "Cannot requeue, the media is processing") ||
            (canRequeue && "Re-Queue") ||
            "Cannot requeue without Original File";

        const disabled = queueProcessing || !canRequeue;
        let buttonAttributes = {
            className: "my-3 btn-block",
            color: disabled ? "warning" : "primary",
            disabled: disabled
        }
        if (!disabled) buttonAttributes.onClick = () => onClickRequeue(item);

        return <Button {...buttonAttributes}>{text}</Button>;
    }

    return (
        <>
            <Modal
                isOpen={isOpen}
                backdrop={true}
                toggle={onClose}
                size="xl">
                <ModalHeader toggle={onClose} tag="h3">
                    {`${show} : ${item.id}`}
                </ModalHeader>
                <div>
                    <Card>
                        <CardBody className="py-2">
                            <KeyValueDisplay data={itemData} />
                        </CardBody>
                    </Card>
                    <Card>
                        <CardBody className="py-2">
                            <CardTitle tag="h4" className="mb-2">Original File</CardTitle>
                            {sourcePath ? (
                                <div className="col-12 my-1 text-sm d-flex align-items-center">
                                    <strong className="mr-2">Azure Source:</strong>
                                    <span
                                        id="azure-file-status-dot"
                                        className="mr-2"
                                        style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', backgroundColor: sourceFileExists ? '#2dce89' : '#f5365c', verticalAlign: 'middle', cursor: 'pointer' }}
                                    />
                                    <UncontrolledTooltip target="azure-file-status-dot">
                                        {sourceFileExists ? 'File exists on Azure — can be requeued' : 'File not found on Azure'}
                                    </UncontrolledTooltip>
                                    <a className="mr-2" href={sourcePath} target="_blank" rel="noopener noreferrer">{sourcePath}</a>
                                    <CopyButton copyValue={sourcePath} id="button-original-path" notificationMessage="Azure link copied to clipboard" />
                                </div>
                            ) : (
                                <p className="text-muted mb-0">No original file information available.</p>
                            )}
                        </CardBody>
                    </Card>
                    {videoPlatformData &&
                        <MediaDetails data={videoPlatformData.media} />
                    }
                    <Container>
                        <Row>
                            <Col xs="12" sm="6">
                                {isMuxReady ?
                                    <Button className="my-3 btn-block" color="primary" onClick={() => updateMediaDuration(item.id, show)}>Update duration</Button>
                                    : <Button className="my-3 btn-block" color="warning" disabled>{updateDurationButtonText}</Button>
                                }
                            </Col>
                            <Col xs="12" sm="6">
                                {requeueButton(item)}
                            </Col>
                        </Row>
                    </Container>
                </div>
            </Modal>
        </>
    );
}

export default MediaInfoModal;